(function() {
'use strict';

class GiveawayProductEntries extends HTMLElement {
  static API_URL = 'https://api.giveaway.ninja/api/ui/calculateEntries';
  static TOKEN_KEY = 'giveawayninja_at';
  static TOKEN_MAX_WAIT = 5000;
  static TOKEN_POLL_INTERVAL = 200;
  static DEBOUNCE_DELAY = 400;
  static FETCH_TIMEOUT = 10000;

  connectedCallback() {
    this.abortController = null;
    this.pollTimerId = null;
    this.debounceTimerId = null;
    this.disconnected = false;
    this.listeners = [];

    const giveawayId = this.dataset.giveawayId;
    const productId = this.dataset.productId;
    if (!giveawayId || !productId) return;

    this.giveawayId = giveawayId;
    this.productId = productId;
    this.sectionId = this.dataset.sectionId;
    this.fallbackPrice = parseInt(this.dataset.variantPrice, 10) || 0;
    this.productFormId = this.dataset.productFormId;

    this.initEntries();
  }

  disconnectedCallback() {
    this.disconnected = true;
    if (this.abortController) this.abortController.abort();
    clearTimeout(this.pollTimerId);
    clearTimeout(this.debounceTimerId);
    this.listeners.forEach(([el, event, handler]) => {
      el.removeEventListener(event, handler);
    });
    this.listeners = [];
  }

  async initEntries() {
    const accessToken = await this.getAccessToken();
    if (!accessToken || this.disconnected) return;
    this.accessToken = accessToken;

    const debouncedCalculate = this.debounce(() => this.calculate(), GiveawayProductEntries.DEBOUNCE_DELAY);

    this.calculate();

    const section = this.closest('.shopify-section');

    const form = section
      ? section.querySelector('#product-form-' + this.sectionId)
      : document.getElementById('product-form-' + this.sectionId);
    if (form) {
      const hiddenInput = form.querySelector('input[name="id"]');
      if (hiddenInput) {
        this.listen(hiddenInput, 'change', debouncedCalculate);
      }
    }

    const variantEl = section
      ? section.querySelector('variant-radios, variant-selects')
      : document.querySelector('variant-radios, variant-selects');
    if (variantEl) {
      this.listen(variantEl, 'change', debouncedCalculate);
    }

    const quantityInput = document.getElementById('Quantity-' + this.sectionId);
    if (quantityInput) {
      this.listen(quantityInput, 'change', debouncedCalculate);
    }

    this.listen(window, 'popstate', debouncedCalculate);
  }

  listen(el, event, handler) {
    el.addEventListener(event, handler);
    this.listeners.push([el, event, handler]);
  }

  getAccessToken() {
    return new Promise((resolve) => {
      const startTime = Date.now();
      const poll = () => {
        if (this.disconnected) { resolve(null); return; }
        try {
          const token = localStorage.getItem(GiveawayProductEntries.TOKEN_KEY);
          if (token && token !== 'null') { resolve(token); return; }
          if (Date.now() - startTime >= GiveawayProductEntries.TOKEN_MAX_WAIT) { resolve(null); return; }
          this.pollTimerId = setTimeout(poll, GiveawayProductEntries.TOKEN_POLL_INTERVAL);
        } catch (e) {
          resolve(null);
        }
      };
      poll();
    });
  }

  getVariantId() {
    const form = document.getElementById(this.productFormId);
    if (form) {
      const input = form.querySelector('input[name="id"]');
      if (input && input.value) return input.value;
    }
    const urlVariant = new URLSearchParams(window.location.search).get('variant');
    if (urlVariant) return urlVariant;
    return this.dataset.variantId;
  }

  getQuantity() {
    const input = document.getElementById('Quantity-' + this.sectionId);
    if (input) {
      const val = parseInt(input.value, 10);
      return isNaN(val) || val < 1 ? 1 : val;
    }
    return 1;
  }

  getVariantPrice(variantId) {
    const el = document.querySelector('variant-selects, variant-radios');
    if (el) {
      const script = el.querySelector('script[type="application/json"]');
      if (script) {
        try {
          const variants = JSON.parse(script.textContent);
          const match = variants.find((v) => String(v.id) === String(variantId));
          if (match) return match.price;
        } catch (e) { /* ignore parse errors */ }
      }
    }
    return this.fallbackPrice;
  }

  async calculate() {
    if (this.disconnected) return;

    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    const variantId = this.getVariantId();
    const quantity = this.getQuantity();
    const price = this.getVariantPrice(variantId);

    const timeoutId = setTimeout(() => this.abortController.abort(), GiveawayProductEntries.FETCH_TIMEOUT);

    try {
      // NOTE: Access token passed as query param per Giveaway Ninja API requirements.
      // Prefer Authorization header if their API adds support for it.
      const response = await fetch(
        GiveawayProductEntries.API_URL + '?access_token=' + encodeURIComponent(this.accessToken),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            giveawayId: this.giveawayId,
            productId: String(this.productId),
            variantId: String(variantId),
            quantity: quantity,
            price: price,
          }),
          signal: this.abortController.signal,
        }
      );

      clearTimeout(timeoutId);

      if (this.disconnected) return;
      if (!response.ok) throw new Error('API error: ' + response.status);

      const result = await response.json();
      if (this.disconnected) return;

      if (result.status === 'Success' && result.data) {
        window.giveawayNinjaProductData = result.data;
        document.dispatchEvent(
          new CustomEvent('giveaway:entries-updated', { detail: result.data })
        );
        this.updateBadge(result.data);
      }
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') return;
      console.error('[GiveawayNinja Product] Fetch error:', error);
    }
  }

  updateBadge(data) {
    const badge = document.getElementById('GiveawayBadge-' + this.sectionId);
    if (!badge) return;

    const totalEntries = data.totalEntries || 0;
    if (totalEntries <= 0 || data.isExpired) {
      badge.style.display = 'none';
      return;
    }

    const textEl = badge.querySelector('.giveaway-badge__text');
    if (textEl) {
      const label = badge.dataset.entriesLabel || '{entries} entries';
      textEl.textContent = label.replace('{entries}', totalEntries);
    }
    badge.style.display = '';
  }

  debounce(fn, wait) {
    return () => {
      clearTimeout(this.debounceTimerId);
      this.debounceTimerId = setTimeout(fn, wait);
    };
  }
}

if (!customElements.get('giveaway-product-entries')) {
  customElements.define('giveaway-product-entries', GiveawayProductEntries);
}
})();
